<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="close">
    <div class="modal-card">
      <div class="section-header">
        <h3>New Lead</h3>
        <button class="icon-button" type="button" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <p v-if="error" class="banner banner-error">
          {{ error }}
          <button v-if="duplicateId" type="button" class="use-existing-link" @click="useExistingContact">
            Use existing contact instead
          </button>
        </p>

        <template v-if="step === 'pick'">
          <label class="modal-field">
            <span>Search contacts</span>
            <input v-model="searchTerm" type="text" placeholder="Name, email, or phone" />
          </label>

          <div class="pick-results">
            <button
              v-for="contact in searchResults"
              :key="contact.id"
              type="button"
              class="pick-result-row"
              @click="selectExistingContact(contact)"
            >
              <strong>{{ contact.contact_name }}</strong>
              <span>{{ contact.email || contact.phone || 'No contact info' }}</span>
            </button>

            <p v-if="!searchResults.length" class="pick-empty">No contacts match.</p>
          </div>

          <button type="button" class="new-contact-link" @click="openNewContactStep">
            <v-icon size="16">mdi-account-plus-outline</v-icon>
            Create new contact
          </button>
        </template>

        <template v-else-if="step === 'new-contact'">
          <label class="modal-field">
            <span>Name *</span>
            <input v-model="newContactDraft.contact_name" type="text" placeholder="Jane Doe" />
          </label>
          <label class="modal-field">
            <span>Email</span>
            <input v-model="newContactDraft.email" type="email" placeholder="jane@example.com" />
          </label>
          <label class="modal-field">
            <span>Phone</span>
            <input v-model="newContactDraft.phone" type="text" placeholder="(555) 123-4567" />
          </label>
          <label class="modal-field">
            <span>Address</span>
            <input v-model="newContactDraft.address" type="text" placeholder="123 Main St" />
          </label>
          <label class="modal-field">
            <span>City</span>
            <input v-model="newContactDraft.city" type="text" />
          </label>
          <label class="modal-field">
            <span>Province</span>
            <input v-model="newContactDraft.province" type="text" />
          </label>
          <label class="modal-field">
            <span>Postal Code</span>
            <input v-model="newContactDraft.postal_code" type="text" />
          </label>
          <label class="modal-field">
            <span>Notes</span>
            <textarea v-model="newContactDraft.notes" rows="3" placeholder="Optional"></textarea>
          </label>
        </template>

        <template v-else-if="step === 'lead-details'">
          <p class="lead-for">
            For <strong>{{ selectedContact ? selectedContact.contact_name : newContactDraft.contact_name }}</strong>
            <button type="button" class="change-link" @click="backToPick">change</button>
          </p>

          <label class="modal-field">
            <span>Service</span>
            <input v-model="leadDraft.service" type="text" placeholder="e.g. Panel upgrade" />
          </label>
          <label class="modal-field">
            <span>Source</span>
            <select v-model="leadDraft.source">
              <option v-for="(label, value) in sourceLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </label>
          <label class="modal-field">
            <span>Status</span>
            <select v-model="leadDraft.status">
              <option v-for="(label, value) in statusLabels" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </label>
          <label class="modal-field">
            <span>Message</span>
            <textarea v-model="leadDraft.message" rows="3" placeholder="What are they asking about?"></textarea>
          </label>
        </template>
      </div>

      <div class="modal-actions">
        <button v-if="step === 'new-contact'" class="icon-button" type="button" @click="step = 'pick'">Back</button>
        <button v-if="step === 'lead-details' && !selectedContact" class="icon-button" type="button" @click="step = 'new-contact'">Back</button>
        <button class="icon-button" type="button" @click="close">Cancel</button>
        <button
          v-if="step === 'new-contact'"
          class="send-button"
          type="button"
          :disabled="!newContactDraft.contact_name.trim()"
          @click="confirmNewContact"
        >
          Next
        </button>
        <button
          v-if="step === 'lead-details'"
          class="send-button"
          type="button"
          :disabled="sending"
          @click="submit"
        >
          {{ sending ? 'Creating…' : 'Create lead' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import { statusLabels, sourceLabels } from '@/constants/leads'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  companyId: { type: String, default: '' },
  contacts: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'saved'])

function blankNewContactDraft() {
  return { contact_name: '', email: '', phone: '', address: '', city: '', province: '', postal_code: '', notes: '' }
}

function blankLeadDraft() {
  return { service: '', source: 'manual', message: '', status: 'new' }
}

const step = ref('pick')
const searchTerm = ref('')
const selectedContact = ref(null)
const newContactDraft = ref(blankNewContactDraft())
const leadDraft = ref(blankLeadDraft())
const sending = ref(false)
const error = ref('')
const duplicateId = ref(null)

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    step.value = 'pick'
    searchTerm.value = ''
    selectedContact.value = null
    newContactDraft.value = blankNewContactDraft()
    leadDraft.value = blankLeadDraft()
    error.value = ''
    duplicateId.value = null
  }
)

const searchResults = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()
  return props.contacts
    .filter((contact) => {
      if (!term) return true
      return [contact.contact_name, contact.email, contact.phone]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    })
    .slice(0, 20)
})

function selectExistingContact(contact) {
  selectedContact.value = contact
  error.value = ''
  duplicateId.value = null
  step.value = 'lead-details'
}

function openNewContactStep() {
  selectedContact.value = null
  newContactDraft.value = blankNewContactDraft()
  step.value = 'new-contact'
}

function confirmNewContact() {
  if (!newContactDraft.value.contact_name.trim()) return
  error.value = ''
  duplicateId.value = null
  step.value = 'lead-details'
}

function backToPick() {
  step.value = 'pick'
}

function useExistingContact() {
  const existing = props.contacts.find((contact) => contact.id === duplicateId.value)
  if (!existing) return

  selectedContact.value = existing
  newContactDraft.value = blankNewContactDraft()
  error.value = ''
  duplicateId.value = null
  step.value = 'lead-details'
}

function close() {
  emit('update:modelValue', false)
}

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function submit() {
  if (sending.value) return

  sending.value = true
  error.value = ''
  duplicateId.value = null

  try {
    const headers = await authHeaders()
    const body = {
      company_id: props.companyId,
      service: leadDraft.value.service.trim() || undefined,
      source: leadDraft.value.source,
      message: leadDraft.value.message.trim() || undefined,
      status: leadDraft.value.status,
      ...(selectedContact.value
        ? { contact_id: selectedContact.value.id }
        : {
            new_contact: {
              contact_name: newContactDraft.value.contact_name.trim(),
              email: newContactDraft.value.email.trim() || undefined,
              phone: newContactDraft.value.phone.trim() || undefined,
              address: newContactDraft.value.address.trim() || undefined,
              city: newContactDraft.value.city.trim() || undefined,
              province: newContactDraft.value.province.trim() || undefined,
              postal_code: newContactDraft.value.postal_code.trim() || undefined,
              notes: newContactDraft.value.notes.trim() || undefined,
            },
          }),
    }

    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))

    if (response.status === 409 && data.existing_id) {
      error.value = data.error || 'A contact with this email already exists.'
      duplicateId.value = data.existing_id
      return
    }

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to create lead.')
    }

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.create',
      label: `Created lead for ${data.lead.contact_name}`,
      company_id: props.companyId,
      metadata: { lead_id: data.lead.id, contact_id: data.lead.contact_id },
    })

    emit('saved', data.lead)
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err.message || 'Something went wrong.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.use-existing-link {
  padding: 0;
  border: 0;
  background: none;
  color: #8a2b1f;
  font: inherit;
  font-weight: 750;
  text-decoration: underline;
  cursor: pointer;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
}

.send-button {
  min-width: 130px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  align-self: end;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.send-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 35, 31, 0.45);
}

.modal-card {
  width: min(440px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 24px 60px rgba(23, 35, 31, 0.25);
}

.modal-body {
  display: grid;
  gap: 12px;
  padding: 16px;
  overflow: auto;
}

.modal-field {
  display: grid;
  gap: 5px;
}

.modal-field span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.modal-field input,
.modal-field select {
  height: 40px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.modal-field textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 11px 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
}

.pick-results {
  display: grid;
  gap: 6px;
  max-height: 260px;
  overflow: auto;
}

.pick-result-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  text-align: left;
  cursor: pointer;
}

/* #f3faf7 is a fixed light tint, not theme-reactive — pin the row's own
   text (the bare <strong>) and its .span child both to a fixed dark pair,
   or they inherit var(--text-primary)/var(--text-secondary) and go
   near-white on a light background in dark mode. */
.pick-result-row:hover {
  border-color: #9fc8ba;
  background: #f3faf7;
  color: #17231f;
}

.pick-result-row span {
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.pick-result-row:hover span {
  color: #56635e;
}

.pick-empty {
  padding: 10px 2px;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.new-contact-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  border: 1px dashed #9fc8ba;
  border-radius: 8px;
  color: #1f5c4d;
  background: #f3faf7;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 750;
  cursor: pointer;
}

.new-contact-link:hover {
  background: #e6f4ef;
}

.lead-for {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.lead-for strong {
  color: var(--text-primary);
}

.change-link {
  margin-left: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: #1f7a63;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 750;
  text-decoration: underline;
  cursor: pointer;
}
</style>
